import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Briefcase, FileText, Download, Mail, Upload, X, RefreshCw, FileArchive, FileBarChart, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

export default function AccountantPack() {
  const fileInputRef = useRef(null);

  const today = new Date();
  // Default to the PREVIOUS month (you usually run this after the month ends)
  const defaultMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const defaultYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [bankFile, setBankFile] = useState(null); // { filename, b64 }
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [emailFormat, setEmailFormat] = useState("both");
  const [busy, setBusy] = useState("");
  const [lastSent, setLastSent] = useState(null);

  const refresh = async () => {
    setLoadingPreview(true);
    try {
      const [pv, st] = await Promise.all([
        axios.get(`${API}/accountant-pack/preview?year=${year}&month=${month}`),
        axios.get(`${API}/accountant-pack/settings`),
      ]);
      setPreview(pv.data);
      if (!recipient) setRecipient(st.data.default_recipient_email || "");
      setLastSent(st.data);
    } catch (e) {
      toast.error("Failed to load preview: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [year, month]);

  const handleBankUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Bank statement must be under 15 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = dataUrl.split(",", 2)[1];
      setBankFile({ filename: file.name, b64 });
      toast.success(`Bank statement loaded: ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  const buildPayload = () => {
    const p = { year, month };
    if (bankFile) {
      p.bank_statement_b64 = bankFile.b64;
      p.bank_statement_filename = bankFile.filename;
    }
    return p;
  };

  const downloadFile = async (kind) => {
    setBusy(kind);
    const startedAt = Date.now();
    let toastId;
    try {
      // Kick off background build
      const startRes = await axios.post(`${API}/accountant-pack/start`, {
        ...buildPayload(),
        format: kind,
      });
      const jobId = startRes.data.job_id;
      toastId = toast.loading(`Building ${kind.toUpperCase()}… 0s`, { duration: 600000 });

      // Poll up to 5 minutes
      const deadline = Date.now() + 5 * 60 * 1000;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() > deadline) throw new Error("Build timed out after 5 minutes");
        await new Promise(r => setTimeout(r, 2000));
        const st = await axios.get(`${API}/accountant-pack/job/${jobId}`);
        const { status, elapsed_seconds, error } = st.data;
        if (status === "error") throw new Error(error || "Build failed");
        toast.loading(`Building ${kind.toUpperCase()}… ${Math.round(elapsed_seconds)}s`, { id: toastId });
        if (status === "done") break;
      }

      // Trigger browser download from the job endpoint
      const dl = await axios.get(
        `${API}/accountant-pack/job/${jobId}/download`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([dl.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `accountant_pack_${year}-${String(month).padStart(2, "0")}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(
        `${kind.toUpperCase()} ready (${Math.round((Date.now() - startedAt) / 1000)}s)`,
        { id: toastId }
      );
    } catch (e) {
      toast.error(
        "Download failed: " + (e.response?.data?.detail || e.message),
        { id: toastId }
      );
    } finally {
      setBusy("");
    }
  };

  const saveDefaultRecipient = async () => {
    try {
      await axios.patch(`${API}/accountant-pack/settings`, { default_recipient_email: recipient });
      toast.success("Default recipient saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const sendEmail = async () => {
    if (!recipient || !recipient.includes("@")) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    if (!window.confirm(
      `Send accountant pack for ${MONTHS[month - 1]} ${year} to:\n${recipient}\n\n` +
      `Format: ${emailFormat.toUpperCase()}${bankFile ? `\nAttached bank statement: ${bankFile.filename}` : ""}\n\n` +
      `The pack will be built and sent in the background — you can close this page after clicking OK.`
    )) return;
    setBusy("email");
    try {
      await axios.post(`${API}/accountant-pack/email`, {
        ...buildPayload(),
        recipient_email: recipient,
        format: emailFormat,
        note,
      });
      toast.success(
        `Pack queued — building and sending to ${recipient} in the background. Check the inbox in a minute.`,
        { duration: 7000 }
      );
      await refresh();
    } catch (e) {
      toast.error("Email failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="accountant-pack-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Briefcase className="h-6 w-6 text-purple-600" />
            Monthly Accountant Pack
          </h1>
          <p className="text-gray-500">
            One click → everything your accountant needs for the month, bundled & emailed.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loadingPreview}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loadingPreview ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Period picker */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">1. Select Period</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 max-w-md">
          <div>
            <Label>Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">2. Pack Preview</CardTitle>
          <CardDescription>
            What will be included for <strong>{MONTHS[month - 1]} {year}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingPreview ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : preview ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Stat label="Suppliers w/ Activity" value={preview.suppliers_with_activity} icon={FileText} />
                <Stat label="Purchase Invoices" value={preview.purchase_invoices} icon={FileText} />
                <Stat label="Open Customers" value={preview.open_customer_accounts} icon={FileText} />
                <Stat label="Open Suppliers" value={preview.open_supplier_accounts} icon={FileText} />
                <Stat label="Uploaded Statements" value={preview.uploaded_supplier_statements} icon={FileText} />
                <Stat label="Uploaded Invoices" value={preview.uploaded_supplier_invoices} icon={FileText} />
              </div>
              {(preview.uploaded_supplier_statements === 0 && preview.uploaded_supplier_invoices === 0) && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                  Tip: To include suppliers' <strong>own</strong> statement / invoice PDFs in the pack,
                  upload them on the{" "}
                  <a className="underline font-medium" href="/supplier-documents">Supplier Documents</a> page.
                  System-generated summaries are still included automatically.
                </div>
              )}
            </>
          ) : null}
        </CardContent>
      </Card>

      {/* Bank statement upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">3. Bank Statement (optional)</CardTitle>
          <CardDescription>
            Attach the bank statement your bank emailed you. Accepted: PDF, CSV, XLSX. Max 15 MB.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.csv,.xlsx,.xls,.txt"
            onChange={handleBankUpload}
            className="hidden"
            data-testid="bank-file-input"
          />
          {bankFile ? (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded p-3">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">{bankFile.filename}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setBankFile(null)} data-testid="clear-bank-file">
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="upload-bank-btn">
              <Upload className="h-4 w-4 mr-2" /> Choose File
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Generate & download */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">4. Generate & Download</CardTitle>
          <CardDescription>Pick a format. The pack is generated fresh on every click.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button onClick={() => downloadFile("zip")} disabled={!!busy} data-testid="download-zip-btn">
            <FileArchive className="h-4 w-4 mr-2" />
            {busy === "zip" ? "Building…" : "Download ZIP"}
          </Button>
          <Button onClick={() => downloadFile("pdf")} disabled={!!busy} variant="secondary" data-testid="download-pdf-btn">
            <FileBarChart className="h-4 w-4 mr-2" />
            {busy === "pdf" ? "Building…" : "Download Combined PDF"}
          </Button>
        </CardContent>
      </Card>

      {/* Email */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">5. Email to Accountant</CardTitle>
          <CardDescription>
            Sends the pack as an email attachment via Resend.{" "}
            {lastSent?.last_sent && (
              <span className="text-green-700">
                Last sent: {new Date(lastSent.last_sent).toLocaleString()}{" "}
                ({lastSent.last_period})
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 max-w-2xl">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label>Recipient email</Label>
              <Input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="accountant@example.co.za"
                data-testid="recipient-email-input"
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={saveDefaultRecipient} disabled={!recipient}>
                Save as default
              </Button>
            </div>
          </div>
          <div>
            <Label>Format</Label>
            <Select value={emailFormat} onValueChange={setEmailFormat}>
              <SelectTrigger className="max-w-xs" data-testid="email-format-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="both">Both — ZIP + Combined PDF</SelectItem>
                <SelectItem value="zip">ZIP only</SelectItem>
                <SelectItem value="pdf">Combined PDF only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note to accountant (optional)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything you want them to know about this month…"
              rows={3}
            />
          </div>
          <Button
            onClick={sendEmail}
            disabled={!!busy || !recipient}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="send-email-btn"
          >
            <Mail className="h-4 w-4 mr-2" />
            {busy === "email" ? "Sending…" : `Send to ${recipient || "..."}`}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-purple-50 border border-purple-200 rounded p-3">
      <div className="text-xs text-purple-700 flex items-center gap-1">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-2xl font-bold text-purple-900 font-mono">{value ?? "—"}</div>
    </div>
  );
}
